import React, { useEffect, useState } from "react";
import Papa from "papaparse";
import useApi from "../useApi";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";

const PredictionTable = ({
    projectId,
    packageId,
    subcontractorId,
    documentId,
}) => {
    const [predictionData, setPredictionData] = useState([]);
    const [latestPredictionStatus, setLatestPredictionStatus] = useState("");
    const [{ data, loading, error }, callApi] = useApi();
    const baseUrl = `/api/projects/${projectId}/packages/${packageId}/subcontractors/${subcontractorId}/documents/${documentId}/predictions`;

    const parseCsvData = (csvString) => {
        const { data: parsedData, errors } = Papa.parse(csvString, {
            header: true,
            skipEmptyLines: true,
        });

        if (errors.length) {
            console.error("Errors while parsing CSV data:", errors);
            errors.forEach((error) => {
                console.error(`Error at row ${error.row}: ${error.message}`);
            });
        }

        return parsedData;
    };

    const fetchLatestPrediction = async () => {
        try {
            const response = await callApi({ url: baseUrl });
            if (response && Array.isArray(response) && response.length > 0) {
                // Sort predictions by processing date in descending order
                const sortedPredictions = response.sort(
                    (a, b) =>
                        new Date(b.processing_date) -
                        new Date(a.processing_date),
                );

                // Find the latest prediction with SUCCESS status
                const latestSuccessPrediction = sortedPredictions.find(
                    (prediction) => prediction.status === "SUCCESS",
                );

                // Use the latest SUCCESS prediction, or the latest overall prediction if none exist
                const latestPrediction =
                    latestSuccessPrediction || sortedPredictions[0];

                setLatestPredictionStatus(latestPrediction.status);

                if (latestPrediction.status === "SUCCESS") {
                    const csvContent = latestPrediction.processed_result;
                    console.log("Received CSV data:", csvContent);
                    const parsedData = parseCsvData(csvContent);

                    setPredictionData(parsedData);
                    console.log("Parsed data:", parsedData);
                } else {
                    setPredictionData([]);
                }
            } else {
                console.error(
                    "No predictions found or invalid response:",
                    response,
                );
                setLatestPredictionStatus("");
                setPredictionData([]);
            }
        } catch (error) {
            console.error("Error fetching predictions:", error);
            setLatestPredictionStatus("");
            setPredictionData([]);
        }
    };

    useEffect(() => {
        fetchLatestPrediction();
    }, [projectId, packageId, subcontractorId, documentId]);

    if (loading) return <div>Loading predictions...</div>;
    if (error) return <div>Error loading predictions: {error.message}</div>;

    // Handle different prediction statuses
    switch (latestPredictionStatus) {
        case "FAILURE":
            return (
                <div>
                    There was an unknown failure processing the quote, but we
                    are working continuously to improve our AI system.
                </div>
            );
        case "UNPROCESSABLE":
            return (
                <div>
                    Our AI system is unable to extract relevant quote data as of
                    now, but we are working continuously to improve our AI
                    system.
                </div>
            );
        case "SUCCESS":
            return (
                <Box
                    className="ai-suggestions-panel"
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <h3>AI Suggestion</h3>
                    <TableContainer
                        component={Paper}
                        sx={{ flexGrow: 1, overflow: "auto" }}
                    >
                        <Table stickyHeader aria-label="AI suggestions table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Quantity</TableCell>
                                    <TableCell>Unit</TableCell>
                                    <TableCell>Rate</TableCell>
                                    <TableCell>Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {predictionData.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            {item.description}
                                        </TableCell>
                                        <TableCell>{item.quantity}</TableCell>
                                        <TableCell>{item.unit}</TableCell>
                                        <TableCell>{item.rate}</TableCell>
                                        <TableCell>{item.total}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            );
        default:
            return (
                <div>
                    Results should be shown here soon. Refresh the page in a
                    minute to get the suggestions.
                </div>
            );
    }
};

export default PredictionTable;
